/**
 * Invoice List Sub-components
 *
 * Loading skeleton table, empty state, and table content for InvoiceList.
 */

import { Table, Skeleton, FilterBar, Card, Summary , Button } from '@elsapiens/ui'

import {
  typeFieldOptions,
  statusFieldOptions,
  paymentStatusFieldOptions,
} from './invoiceListHelpers'
import { formatAmount } from '../../../types/billing'
import { NumberedPagination } from '../../ui'


import type { Invoice } from '../../../types/billing'
import type { TableColumn, FilterBarCondition } from '@elsapiens/ui'

// ── Loading skeleton ────────────────────────────────────────────

export function InvoiceLoadingTable({ isVendor }: { isVendor: boolean }): JSX.Element {
  const skeletonCols: TableColumn<Record<string, unknown>>[] = [
    { key: 'a', header: 'Invoice #', render: () => <Skeleton height="1.5rem" width="100px" /> },
    { key: 'b', header: 'Date', render: () => <Skeleton height="1.5rem" width="80px" /> },
    { key: 'c', header: 'Type', render: () => <Skeleton height="1.25rem" width="80px" /> },
    { key: 'd', header: isVendor ? 'Supplier' : 'Customer', render: () => <Skeleton height="1.5rem" width="120px" /> },
    { key: 'e', header: 'Taxable', render: () => <Skeleton height="1.5rem" width="70px" /> },
    { key: 'f', header: 'Tax', render: () => <Skeleton height="1.5rem" width="60px" /> },
    { key: 'g', header: 'Total', render: () => <Skeleton height="1.5rem" width="70px" /> },
    { key: 'h', header: 'Status', render: () => <Skeleton height="1.25rem" width="60px" /> },
    { key: 'i', header: 'Actions', render: () => <Skeleton height="1.5rem" width="80px" /> },
  ]

  return (
    <Table
      columns={skeletonCols}
      data={Array.from({ length: 5 }, (_, i) => ({ id: String(i) }))}
      hoverable={false}
      inCard="last"
    />
  )
}

// ── Empty state ─────────────────────────────────────────────────

interface InvoiceEmptyStateProps {
  heading: string
  description: string
  hasFilters: boolean
  buttonLabel: string
  onClearFilters: () => void
  onCreate: () => void
  customNode?: React.ReactNode
}

export function InvoiceEmptyState({
  heading,
  description,
  hasFilters,
  buttonLabel,
  onClearFilters,
  onCreate,
  customNode,
}: InvoiceEmptyStateProps): JSX.Element {
  if (customNode) { return customNode as JSX.Element }

  return (
    <div className="el-p-xl text-center flex flex-col items-center el-gap-field">
      <svg className="w-16 h-16 text-foreground-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
        />
      </svg>
      <h3 className="text-lg font-medium text-foreground">{heading}</h3>
      <p className="text-foreground-secondary">{description}</p>
      {hasFilters ? (
        <Button variant="secondary" onClick={onClearFilters}>Clear filters</Button>
      ) : (
        <Button variant="primary" onClick={onCreate}>{buttonLabel}</Button>
      )}
    </div>
  )
}

// ── Table content (with numbered pagination) ────────────────────

interface InvoiceTableContentProps {
  filteredInvoices: Invoice[]
  columns: TableColumn<Invoice>[]
  page: number
  totalPages: number
  totalCount: number
  isLoadingMore?: boolean
  onPageChange: (p: number) => void
  onView: (inv: Invoice) => void
}

export function InvoiceTableContent({
  filteredInvoices,
  columns,
  page,
  totalPages,
  totalCount,
  isLoadingMore,
  onPageChange,
  onView,
}: InvoiceTableContentProps): JSX.Element {
  return (
    <>
      <Table
        hoverable
        columns={columns}
        data={filteredInvoices}
        inCard={totalPages > 1 ? 'first' : 'last'}
        onRowClick={(inv: Invoice) => onView(inv)}
      />
      {totalPages > 1 && (
        <NumberedPagination
          isLoading={isLoadingMore}
          page={page}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      )}
    </>
  )
}

// ── Summary bar ─────────────────────────────────────────────────

interface InvoiceSummaryBarProps {
  count: number
  totalAmount: number
  totalTax: number
  baseCurrency: string
}

export function InvoiceSummaryBar({ count, totalAmount, totalTax, baseCurrency }: InvoiceSummaryBarProps): JSX.Element {
  return (
    <Summary
      className="el-mb-lg"
      items={[
        { label: 'Invoices', value: String(count) },
        { label: 'Total', value: formatAmount(totalAmount, baseCurrency) },
        { label: 'Tax', value: formatAmount(totalTax, baseCurrency) },
      ]}
    />
  )
}

// ── Filter bar ──────────────────────────────────────────────────

interface InvoiceFilterBarProps {
  activeTab: string
  conditions: FilterBarCondition[]
  searchQuery: string
  sortField: string
  sortDirection: 'asc' | 'desc'
  currencyOptions: { value: string; label: string }[]
  partyLabel: string
  isVendor: boolean
  onSearchChange: (q: string) => void
  onConditionsChange: (c: FilterBarCondition[]) => void
  onSortChange: (id: string, dir: 'asc' | 'desc') => void
  onTabChange: (tabId: string) => void
}

const TAB_DEFS = [
  { id: 'all', conditions: [] as FilterBarCondition[] },
  { id: 'unpaid', conditions: [{ id: 'tab-paymentStatus', fieldId: 'paymentStatus', operator: 'equals' as const, value: 'unpaid' }] },
  { id: 'partial', conditions: [{ id: 'tab-paymentStatus', fieldId: 'paymentStatus', operator: 'equals' as const, value: 'partial' }] },
]

function buildFilterFields(currencyOptions: { value: string; label: string }[]): Array<{ id: string; label: string; type: 'select' | 'date'; options?: { value: string; label: string }[] }> {
  return [
    { id: 'invoiceType', label: 'Type', type: 'select', options: typeFieldOptions },
    { id: 'status', label: 'Status', type: 'select', options: statusFieldOptions },
    { id: 'paymentStatus', label: 'Payment Status', type: 'select', options: paymentStatusFieldOptions },
    ...(currencyOptions.length > 1 ? [{ id: 'currency', label: 'Currency', type: 'select' as const, options: currencyOptions }] : []),
    { id: 'invoiceDate', label: 'Date', type: 'date' },
  ]
}

function getSortOptions(partyLabel: string): Array<{ id: string; label: string }> {
  return [
    { id: 'invoiceDate', label: 'Date' },
    { id: 'invoiceNumber', label: 'Invoice #' },
    { id: 'partyName', label: partyLabel },
    { id: 'totalAmount', label: 'Amount' },
  ]
}

const FILTER_BAR_TABS = [
  { id: 'all', label: 'All', conditions: [] },
  { id: 'unpaid', label: 'Unpaid', conditions: [{ id: 'tab-paymentStatus', fieldId: 'paymentStatus', operator: 'equals' as const, value: 'unpaid' }] },
  { id: 'partial', label: 'Partial', conditions: [{ id: 'tab-paymentStatus', fieldId: 'paymentStatus', operator: 'equals' as const, value: 'partial' }] },
]

export function InvoiceFilterBar(p: InvoiceFilterBarProps): JSX.Element {
  const handleTabChange = (tabId: string): void => {
    p.onTabChange(tabId)
    const tab = TAB_DEFS.find(t => t.id === tabId)
    const userConditions = p.conditions.filter(c => !c.id.startsWith('tab-'))
    p.onConditionsChange([...userConditions, ...(tab?.conditions ?? [])])
  }

  return (
    <FilterBar
      firstInCard
      inCard
      activeTab={p.activeTab}
      conditions={p.conditions}
      fields={buildFilterFields(p.currencyOptions)}
      search={{
        value: p.searchQuery,
        onChange: p.onSearchChange,
        // Kept short deliberately — the shared FilterBar's search input has a
        // fixed width (--el-dropdown-width-sm, ~192px default density) and a
        // longer placeholder ("Search by invoice #, customer, or GSTIN...")
        // gets visually clipped by the input's own boundary before it fits.
        placeholder: p.isVendor ? 'Search bills...' : 'Search invoices...',
      }}
      sort={{
        options: getSortOptions(p.partyLabel),
        value: p.sortField,
        direction: p.sortDirection,
        onChange: p.onSortChange,
      }}
      tabs={FILTER_BAR_TABS}
      onConditionsChange={p.onConditionsChange}
      onTabChange={handleTabChange}
    />
  )
}

// ── Card body combining filter + table content ──────────────────

interface InvoiceCardBodyProps {
  isLoading: boolean
  filteredInvoices: Invoice[]
  columns: TableColumn<Invoice>[]
  activeTab: string
  conditions: FilterBarCondition[]
  searchQuery: string
  sortField: string
  sortDirection: 'asc' | 'desc'
  currencyOptions: { value: string; label: string }[]
  partyLabel: string
  isVendor: boolean
  hasFilters: boolean
  emptyStateNode?: React.ReactNode
  emptyStateHeading: string
  emptyStateDescription: string
  emptyStateButtonLabel: string
  page: number
  totalPages: number
  totalCount: number
  isLoadingMore?: boolean
  onPageChange: (p: number) => void
  onView: (inv: Invoice) => void
  onCreate: () => void
  onSearchChange: (q: string) => void
  onConditionsChange: (c: FilterBarCondition[]) => void
  onSortChange: (id: string, dir: 'asc' | 'desc') => void
  onTabChange: (tabId: string) => void
  onClearFilters: () => void
}

function renderTableSection(p: InvoiceCardBodyProps): React.ReactNode {
  if (p.isLoading) {
    return <InvoiceLoadingTable isVendor={p.isVendor} />
  }
  if (p.filteredInvoices.length === 0) {
    return (
      <InvoiceEmptyState
        buttonLabel={p.emptyStateButtonLabel}
        customNode={p.emptyStateNode}
        description={p.emptyStateDescription}
        hasFilters={p.hasFilters}
        heading={p.emptyStateHeading}
        onClearFilters={p.onClearFilters}
        onCreate={p.onCreate}
      />
    )
  }
  return (
    <InvoiceTableContent
      columns={p.columns}
      filteredInvoices={p.filteredInvoices}
      isLoadingMore={p.isLoadingMore}
      page={p.page}
      totalCount={p.totalCount}
      totalPages={p.totalPages}
      onPageChange={p.onPageChange}
      onView={p.onView}
    />
  )
}

export function InvoiceCardBody(p: InvoiceCardBodyProps): JSX.Element {
  return (
    <Card className="overflow-hidden">
      <InvoiceFilterBar
        activeTab={p.activeTab}
        conditions={p.conditions}
        currencyOptions={p.currencyOptions}
        isVendor={p.isVendor}
        partyLabel={p.partyLabel}
        searchQuery={p.searchQuery}
        sortDirection={p.sortDirection}
        sortField={p.sortField}
        onConditionsChange={p.onConditionsChange}
        onSearchChange={p.onSearchChange}
        onSortChange={p.onSortChange}
        onTabChange={p.onTabChange}
      />
      {renderTableSection(p)}
    </Card>
  )
}
